import type { IntlShape } from 'react-intl';

import type { DraftPresentation } from '../../contracts/approval-kinds';

export type DraftHeaderKind =
  | 'writing'
  | 'note'
  | 'context'
  | 'document'
  | 'edit'
  | 'context_document'
  | 'context_edit';

export type DraftHeaderSpec = {
  kind: DraftHeaderKind;
  title?: string;
};

const DRAFT_HEADER_TITLE_MAX = 40;

export const truncateDraftHeaderTitle = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > DRAFT_HEADER_TITLE_MAX
    ? `${trimmed.slice(0, DRAFT_HEADER_TITLE_MAX - 1)}…`
    : trimmed;
};

// The context-document tools share the `session_document` presentation,
// so only the tool name says the draft goes to the Context tab.
const CONTEXT_DOCUMENT_TOOLS: ReadonlySet<string> = new Set([
  'create_session_context_document',
  'update_session_context_document',
]);

/**
 * Pending-card title by destination. Streaming stays "Writing…".
 * Create (`title_key`, no `document_id_key`) vs edit (`document_id_key`)
 * share `session_document` but must not both say "Edit note". An edit names
 * its document from the input, or else from `documentTitle`.
 */
export const resolveDraftHeaderSpec = (
  status: 'streaming_input' | 'pending_approval',
  draft: DraftPresentation | null,
  toolInput: Record<string, unknown>,
  { toolName, documentTitle }: { toolName?: string; documentTitle?: string } = {}
): DraftHeaderSpec => {
  if (status === 'streaming_input') return { kind: 'writing' };
  if (draft?.target === 'session_context') return { kind: 'context' };
  if (draft?.target === 'session_document') {
    const inputTitle = draft.title_key
      ? truncateDraftHeaderTitle(toolInput[draft.title_key])
      : undefined;
    const isContext = toolName !== undefined && CONTEXT_DOCUMENT_TOOLS.has(toolName);
    if (draft.document_id_key) {
      return {
        kind: isContext ? 'context_edit' : 'edit',
        title: inputTitle ?? truncateDraftHeaderTitle(documentTitle),
      };
    }
    return { kind: isContext ? 'context_document' : 'document', title: inputTitle };
  }
  return { kind: 'note' };
};

/** Localized card title for a {@link DraftHeaderSpec}. */
export const formatDraftHeader = (intl: IntlShape, spec: DraftHeaderSpec): string => {
  switch (spec.kind) {
    case 'writing':
      return intl.formatMessage({ defaultMessage: 'Writing…', id: 'Kd68snacT9' });
    case 'context':
      return intl.formatMessage({
        defaultMessage: 'Edit context',
        id: 'eAcA4h/Xgf',
        description: 'Header of a draft card that edits the existing context page',
      });
    case 'edit':
      return spec.title
        ? intl.formatMessage(
            { defaultMessage: 'Edit document: {title}', id: '+8lMp3WFI5' },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Edit document',
            id: '+68jbgdAms',
          });
    case 'document':
      return spec.title
        ? intl.formatMessage(
            {
              defaultMessage: 'Draft document: {title}',
              id: 'o34BBS3vZ9',
            },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Draft document',
            id: 'Wjdr2OREQ8',
          });
    case 'context_document':
      return spec.title
        ? intl.formatMessage(
            {
              defaultMessage: 'Draft context document: {title}',
              id: 'UXSRH66Cad',
              description:
                'Header of a draft card for a new context document, with its name',
            },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Draft context document',
            id: '1AiUQVltIe',
            description: 'Header of a draft card for a new context document',
          });
    case 'context_edit':
      return spec.title
        ? intl.formatMessage(
            {
              defaultMessage: 'Edit context document: {title}',
              id: 'Gy7XraKQhE',
              description:
                'Header of a draft card editing a context document, with its name',
            },
            { title: spec.title }
          )
        : intl.formatMessage({
            defaultMessage: 'Edit context document',
            id: 'cRWzPZh+sC',
            description: 'Header of a draft card editing a context document',
          });
    case 'note':
      return intl.formatMessage({
        defaultMessage: 'Edit note',
        id: 'qZT0ZxSNOa',
        description: 'Header of a draft card that replaces the existing consult note',
      });
  }
};
